import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { reminderApi, memberApi } from '../../services/api';

const ReminderTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [reminder, setReminder] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch reminder details
        const reminderResponse = await reminderApi.getReminderById(id);
        setReminder(reminderResponse.data);
        
        // Fetch members
        const membersResponse = await memberApi.getAll();
        setMembers(membersResponse.data);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Handle test reminder
  const handleTestReminder = async () => {
    if (!selectedMember) {
      setTestResult({
        success: false,
        message: 'Please select a member to test with.'
      });
      return;
    }
    
    setTestLoading(true);
    setTestResult(null);
    
    try {
      await reminderApi.testReminder(id, selectedMember);
      setTestResult({
        success: true,
        message: 'Test reminder sent successfully!'
      });
    } catch (err) {
      console.error('Error testing reminder:', err);
      setTestResult({
        success: false,
        message: `Failed to send test reminder: ${err.response?.data?.detail || err.message}`
      });
    } finally {
      setTestLoading(false);
    }
  };
  
  // Helper function to format reminder trigger
  const formatTrigger = (trigger, days) => {
    switch (trigger) {
      case 'days_before_expiry':
        return `${days} day${days !== 1 ? 's' : ''} before expiry`;
      case 'days_after_expiry':
        return `${days} day${days !== 1 ? 's' : ''} after expiry`;
      case 'specific_day':
        return `On day ${days} of each month`;
      default:
        return trigger;
    }
  };
  
  // Helper function to format reminder frequency
  const formatFrequency = (frequency) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'once':
        return 'Once';
      default:
        return frequency;
    }
  };
  
  // Helper function to format reminder type
  const formatType = (type) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'telegram':
        return 'Telegram';
      default:
        return type;
    }
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!reminder) {
    return (
      <div className="alert alert-danger" role="alert">
        Reminder not found. <Link to="/reminders">Return to reminders list</Link>
      </div>
    );
  }
  
  return (
    <div className="reminder-test">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Test Reminder</h1>
        <div>
          <Link to={`/reminders/${id}/edit`} className="btn btn-outline-primary me-2">
            <i className="fas fa-edit me-1"></i> Edit Reminder
          </Link>
          <Link to="/reminders" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-1"></i> Back to Reminders
          </Link>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="row">
        <div className="col-md-6">
          <div className="card shadow-sm mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Reminder Details</h5>
            </div>
            <div className="card-body">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Name</th>
                    <td>{reminder.name}</td>
                  </tr>
                  <tr>
                    <th>Type</th>
                    <td>{formatType(reminder.reminder_type)}</td>
                  </tr>
                  <tr>
                    <th>Trigger</th>
                    <td>{formatTrigger(reminder.trigger, reminder.trigger_days)}</td>
                  </tr>
                  <tr>
                    <th>Frequency</th>
                    <td>{formatFrequency(reminder.frequency)}</td>
                  </tr>
                  <tr>
                    <th>Send Time</th>
                    <td>{reminder.send_time}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <span className={`badge ${reminder.enabled ? 'bg-success' : 'bg-danger'}`}>
                        {reminder.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Last Run</th>
                    <td>{reminder.last_run ? new Date(reminder.last_run).toLocaleDateString() : 'Never'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-header">
              <h5 className="card-title mb-0">Test Reminder</h5>
            </div>
            <div className="card-body">
              <p className="card-text">
                Select a member to send a test reminder to. This will process the reminder immediately for the selected member.
              </p>
              
              <div className="mb-3">
                <label htmlFor="memberSelect" className="form-label">Select Member</label>
                <select
                  id="memberSelect"
                  className="form-select"
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  disabled={testLoading}
                >
                  <option value="">Select a member</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name || member.username} {member.email && `(${member.email})`}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                className="btn btn-primary"
                onClick={handleTestReminder}
                disabled={testLoading || !selectedMember}
              >
                {testLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Sending Test...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-1"></i> Send Test Reminder
                  </>
                )}
              </button>
              
              {testResult && (
                <div className={`alert mt-3 ${testResult.success ? 'alert-success' : 'alert-danger'}`} role="alert">
                  {testResult.message}
                </div>
              )}
              
              <div className="mt-3">
                <h6>Important Notes:</h6>
                <ul className="small">
                  <li>Test reminders are sent immediately, regardless of the configured send time.</li>
                  <li>Test reminders are not logged in the reminder logs.</li>
                  <li>For email reminders, the member must have a valid email address.</li>
                  <li>For Telegram reminders, the member must have a valid Telegram chat ID.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReminderTest;
